import {
    ESidebarItems,
    ModalType,
    SidebarItem,
    SidebarItems,
} from "./SidebarTypes";

import { GoPeople } from "react-icons/go";
import { FaMapPin } from "react-icons/fa";
import { RiAddFill } from "react-icons/ri";
import { LuCalendarDays } from "react-icons/lu";
import { PiShieldStarBold } from "react-icons/pi";
import { IoSettingsOutline } from "react-icons/io5";
import { RiDashboardHorizontalLine } from "react-icons/ri";

const COMMUNITY_MANAGEMENT = "community-management";

const getSectionItems = (
    updateModalType: (type: ModalType) => void,
    actualSlug: string,
    items: string[],
) => {
    const sidebarItemsMap = getSideBarItemsMap(updateModalType, actualSlug);

    return items
        .map((itemName) => sidebarItemsMap[itemName])
        .filter((item) => !!item); // filter undefined values
};

// NB: change in the structure below allows us to just save the string and get items from our record based on the key value.
/**
 * Generates the sidebar items for a given community slug.
 **/
export const getSideBarItems = (
    actualSlug: string,
    updateModalType: (type: ModalType) => void,
    pinnedItems?: string[],
) => {
    return {
        pinnedItems: {
            title: "Pinned Items",
            Icon: FaMapPin,
            items: pinnedItems
                ? getSectionItems(updateModalType, actualSlug, pinnedItems)
                : [],
        },
        events: {
            title: "Events",
            Icon: LuCalendarDays,
            items: getSectionItems(updateModalType, actualSlug, [
                "Create new event",
                "Create multiple events",
                "Event Images",
                "Host applications",
                "Reimbursement Requests",
                "Preapproved hosts",
                "Proposed events",
                "Event Templates",
                "Batch edit",
                "Ticket Packages",
                "Image Overlays",
                "Sponsors",
            ]),
        },
        community: {
            title: "Community",
            Icon: GoPeople,
            titleHref: `/${actualSlug}`,
            items: getSectionItems(updateModalType, actualSlug, [
                "Community Managers",
                "Badges",
                "Questionnaires",
                "Merchandise",
                "Send messages",
                "Message Customizations",
                "Sponsor Applications",
                "Sponsorship Packages",
                "Invitations",
                "Partners",
            ]),
        },
        setupAndSettings: {
            title: "Setup & Settings",
            Icon: IoSettingsOutline,
            titleHref: `/${COMMUNITY_MANAGEMENT}/${actualSlug}/settings`,
            items: getSectionItems(updateModalType, actualSlug, [
                "Community URL",
                "Community settings",
                "Roles / Permissions",
                "Migrate Lu.ma events",
            ]),
        },
        management: {
            title: "Dashboard",
            Icon: RiDashboardHorizontalLine,
            titleHref: `/${COMMUNITY_MANAGEMENT}/${actualSlug}/dashboards`,
            items: getSectionItems(updateModalType, actualSlug, [
                "Feedback",
                "Tags",
                "Download all data (CSV)",
                "Monthly Stats",
                "Events Data",
                "Hosts",
                "Events",
                "Attendees",
                "Users",
                "$ Earnings",
                "Message log",
                "Monthly Chapter Stats",
            ]),
        },
        memberships: {
            title: "Memberships",
            Icon: PiShieldStarBold,
            titleHref: `/${COMMUNITY_MANAGEMENT}/${actualSlug}/memberships`,
            items: getSectionItems(updateModalType, actualSlug, [
                "Membership chapters",
                "Manage members",
                "Referrals",
            ]),
        },
    };
};

/** Record of all sidebar Items */
export const getSideBarItemsMap = (
    updateModalType: (type: ModalType) => void,
    slug: string,
): Record<string, SidebarItem> => {
    return {
        "Create new event": {
            name: "Create new event",
            Icon: RiAddFill,
            isSpecial: true,
            onClick: () => updateModalType("new-event"),
        },
        "Create multiple events": {
            name: "Create multiple events",
            Icon: RiAddFill,
            isSpecial: true,
            onClick: () => updateModalType("batch-events"),
        },
        "Event Images": {
            name: "Event Images",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/images`,
        },
        "Host applications": {
            name: "Host applications",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/host-applications`,
        },
        "Reimbursement Requests": {
            name: "Reimbursement Requests",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/reimbursement-requests`,
        },
        "Preapproved hosts": {
            name: "Preapproved hosts",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/host-preapproval`,
        },
        "Proposed events": {
            name: "Proposed events",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/event-proposals`,
        },
        "Event Templates": {
            name: "Event Templates",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/event-templates`,
        },
        "Batch edit": {
            name: "Batch edit",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/${ESidebarItems.BATCH_EDIT}`,
        },
        "Ticket Packages": {
            name: "Ticket Packages",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/ticket-packages`,
            showForAdmin: true,
        },
        "Image Overlays": {
            name: "Image Overlays",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/image-overlays`,
        },
        Sponsors: {
            name: "Sponsors",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/sponsors`,
        },
        "Community Managers": {
            name: "Community Managers",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/roles`,
        },
        Badges: {
            name: "Badges",
            onClick: () => updateModalType("badges"),
        },
        Questionnaires: {
            name: "Questionnaires",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/questionnaires`,
        },
        Merchandise: {
            name: "Merchandise",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/merchandise`,
        },
        "Send messages": {
            name: "Send messages",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/send-messages`,
        },
        "Message Customizations": {
            name: "Message Customizations",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/message-customizations`,
        },
        "Sponsor Applications": {
            name: "Sponsor Applications",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/sponsors/applications`,
        },
        "Sponsorship Packages": {
            name: "Sponsorship Packages",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/sponsors/packages`,
        },
        Invitations: {
            name: "Invitations",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/invitations`,
        },
        Partners: {
            name: "Partners",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/partners`,
        },
        "Community URL": {
            name: "Community URL",
            onClick: () => updateModalType("slug"),
        },
        "Community settings": {
            name: "Community settings",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/settings`,
        },
        "Roles / Permissions": {
            name: "Roles / Permissions",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/roles`,
        },
        "Migrate Lu.ma events": {
            name: "Migrate Lu.ma events",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/migration/events`,
        },
        Feedback: {
            name: "Feedback",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/feedback`,
        },
        Tags: {
            name: "Tags",
            href: `/v1/${slug}/tags`,
        },
        "Download all data (CSV)": {
            name: "Download all data (CSV)",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards/download`,
        },
        "Monthly Stats": {
            name: "Monthly Stats",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards/monthly-stats`,
        },
        "Events Data": {
            name: "Events Data",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards`,
        },
        Hosts: {
            name: "Hosts",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards/hosts`,
        },
        Events: {
            name: "Events",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards/events`,
        },
        Attendees: {
            name: "Attendees",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards/attendees`,
        },
        Users: {
            name: "Users",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards/users`,
        },
        "Membership chapters": {
            name: "Membership chapters",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/memberships/tiers`,
        },
        "Manage members": {
            name: "Manage members",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/memberships`,
        },
        "$ Earnings": {
            name: "$ Earnings",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards/earnings`,
        },
        Referrals: {
            name: "Referrals",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/memberships/referrals`,
        },
        "Message log": {
            name: "Message log",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards/message-log`,
        },
        "Monthly Chapter Stats": {
            name: "Monthly Chapter Stats",
            href: `/${COMMUNITY_MANAGEMENT}/${slug}/dashboards/monthly-chapter-stats`,
        },
    };
};

export const getSideBarItemsForPreapprovedHost = (
    actualSlug: string,
    updateModalType: (type: ModalType) => void,
): SidebarItems => {
    return {
        events: {
            title: "Events",
            Icon: LuCalendarDays,
            items: [
                {
                    name: "Create new event",
                    Icon: RiAddFill,
                    isSpecial: true,
                    onClick: () => updateModalType("new-event"),
                },
            ],
        },
        community: {
            title: "Community",
            Icon: GoPeople,
            titleHref: `/${actualSlug}`,
            items: [],
        },
    };
};

export const getSideBarItemsForChapterLead = (
    actualSlug: string,
    updateModalType: (type: ModalType) => void,
): SidebarItems => {
    return {
        events: {
            title: "Events",
            Icon: LuCalendarDays,
            items: [
                {
                    name: "Create new event",
                    Icon: RiAddFill,
                    isSpecial: true,
                    onClick: () => updateModalType("new-event"),
                },
            ],
        },
        community: {
            title: "Community",
            Icon: GoPeople,
            titleHref: `/${actualSlug}`,
            items: [
                {
                    name: "Send messages",
                    href: `/${COMMUNITY_MANAGEMENT}/${actualSlug}/send-messages`,
                },
            ],
        },
    };
};
